import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/primitives";
import { post } from "../lib/api";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { queryClient, useMe } from "../lib/hooks";

/** Mensajes para los códigos `?error=` que devuelve el callback de Google (nunca detalle interno). */
const GOOGLE_ERRORS: Record<string, string> = {
  invalid_state: "La sesión de acceso con Google caducó o no es válida. Inténtalo de nuevo.",
  denied: "Cancelaste el acceso con Google.",
  unverified: "Google no ha verificado el email de esa cuenta.",
  wrong_domain: "Esa cuenta de Google no pertenece al dominio permitido.",
  not_authorized: "Tu cuenta de Google no tiene acceso a la fábrica. Pide a un admin que te dé de alta.",
  failed: "No se pudo completar el acceso con Google. Inténtalo de nuevo.",
};

type AuthConfig = { google: boolean; passwordLogin: "all" | "bootstrap_admin_only" | "off"; allowedDomain: string };

export function LoginPage() {
  const me = useMe();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const authConfig = useQuery({ queryKey: ["auth-config"], queryFn: () => get<AuthConfig>("/auth/config") });
  const cfg = authConfig.data;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const googleErr = params.get("error") ? (GOOGLE_ERRORS[params.get("error")!] ?? GOOGLE_ERRORS.failed) : null;
  const [busy, setBusy] = useState(false);
  if (me.data) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await post("/auth/login", { email, password });
      await queryClient.invalidateQueries();
      nav("/", { replace: true });
    } catch (x) {
      setErr(x instanceof Error ? x.message : "No se pudo iniciar sesión");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4">
      <form onSubmit={submit} className="flex w-full max-w-[400px] flex-col gap-4 rounded-3xl border border-ln2 bg-sf p-7">
        <div className="flex items-center gap-2 font-display text-[22px] font-bold tracking-[-0.02em]"><span className="h-4 w-4 rounded-[5px] bg-ac" />B-Green Factory</div>
        <p className="m-0 text-[14px] text-mu">Misión → plan → ejecución → resultado. Inicia sesión para supervisar la fábrica.</p>
        {googleErr && <div role="alert" className="rounded-card border border-er px-3 py-2 text-[14px] text-er">{googleErr}</div>}
        {cfg?.google && (
          // Navegación completa (no SPA): el backend responde con una redirección a Google.
          <a href="/api/auth/google/start" className="inline-flex h-[52px] items-center justify-center gap-2 rounded-[10px] bg-ac px-6 text-base font-semibold text-acx transition-colors hover:brightness-110">
            Continuar con Google
          </a>
        )}
        {cfg?.google && <p className="m-0 text-center text-[12px] text-mu">Solo cuentas @{cfg.allowedDomain} dadas de alta por un admin.</p>}
        {cfg && cfg.passwordLogin !== "off" && (
          <details open={!cfg.google} className="flex flex-col gap-4">
            {cfg.google && <summary className="mb-4 cursor-pointer text-[13px] text-mu">{cfg.passwordLogin === "bootstrap_admin_only" ? "Acceso de emergencia (solo admin inicial)" : "Acceso con contraseña"}</summary>}
            <div className="flex flex-col gap-4">
              <Field label="Email"><Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
              <Field label="Contraseña"><Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
              {err && <div role="alert" className="rounded-card border border-er px-3 py-2 text-[14px] text-er">{err}</div>}
              <Button type="submit" variant={cfg.google ? "secondary" : "primary"} size="lg" loading={busy}>Entrar</Button>
            </div>
          </details>
        )}
        <DemoHint />
      </form>
    </div>
  );
}

function DemoHint() {
  const health = useQuery({ queryKey: ["health"], queryFn: () => get<{ mode: string }>("/health") });
  if (health.data?.mode !== "demo") return null;
  return <p className="m-0 text-[12px] text-mu">Modo demo: datos simulados. Cuentas de ejemplo en el README (<span className="font-mono">operador@demo.local</span>).</p>;
}
